import { logEvent as firebaseLogEvent, setUserId as firebaseSetUserId } from 'firebase/analytics';
import { initializeFirebase, getAnalyticsInstance } from './config';

// Initialize on first import and export the promise
export const analyticsInitPromise = initializeFirebase();

/**
 * Log a custom analytics event
 * @param {string} eventName - Event name (use constants from analytics/event.js)
 * @param {object} eventParams - Event parameters (optional)
 */
/**
 * Wait for analytics initialization to complete
 * @returns {Promise<void>}
 */
export const waitForAnalytics = async () => {
  await analyticsInitPromise;
};

export const logEvent = (eventName, eventParams = {}) => {
  const analytics = getAnalyticsInstance();
  if (!analytics) {
    return;
  }

  try {
    firebaseLogEvent(analytics, eventName, eventParams);
  } catch (error) {
    console.error('[Analytics] Error logging event:', eventName, error);
  }
};

/**
 * Log a screen view (page transition)
 * @param {string} eventName - Screen/page name
 * @param {object} additionalParams - Additional parameters (optional)
 */
export const logScreenView = (eventName, additionalParams = {}) => {
  logEvent(eventName, {
    ...additionalParams,
  });
};

/**
 * Log a button click event
 * @param {string} eventName - Name of the event name
 * @param {string} buttonName - Name of the button clicked
 * @param {object} additionalParams - Additional parameters (optional)
 */
export const logButtonClick = (eventName, buttonName, additionalParams = {}) => {
  logEvent(eventName, {
    button_name: buttonName,
    ...additionalParams,
  });
};

// Track current user ID to avoid redundant calls
let currentUserId = null;

/**
 * Set user ID for analytics
 * @param {string} userId - User ID to set (pass null to clear)
 */
export const setUserId = (userId) => {
  const analytics = getAnalyticsInstance();
  if (!analytics) {
    return;
  }

  // Convert to string or null
  const userIdString = userId ? String(userId) : null;

  // Skip if already set to the same value
  if (currentUserId === userIdString) {
    return;
  }

  try {
    firebaseSetUserId(analytics, userIdString);
    currentUserId = userIdString;
  } catch (error) {
    console.error('[Analytics] Error setting user ID:', error);
  }
};

/**
 * Get current user ID
 * @returns {string|null} Current user ID
 */
export const getCurrentUserId = () => {
  return currentUserId;
};

/**
 * Clear user ID
 */
export const clearUserId = () => {
  setUserId(null);
};
